/**
 * Digest Preferences View
 * Manage module digest subscriptions and preferences
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../config/msalConfig';
import {
    Card,
    Tabs,
    Space,
    Spin,
    message,
    Alert,
    Typography,
    Divider
} from 'antd';
import {
    MailOutlined,
    SettingOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import {
    getPreferences,
    getAvailableModules,
    getDeliveryHistory
} from '../../services/digestService';
import ModuleSubscriptionCard from './ModuleSubscriptionCard';
import DeliveryScheduleSettings from './DeliveryScheduleSettings';
import DeliveryHistoryTable from './DeliveryHistoryTable';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function DigestPreferences() {
    const { instance, accounts } = useMsal();
    const [loading, setLoading] = useState(true);
    const [preferences, setPreferences] = useState(null);
    const [availableModules, setAvailableModules] = useState([]);
    const [deliveryHistory, setDeliveryHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('subscriptions');

    // Load data on mount
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);

            // Get access token
            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const accessToken = response.accessToken;

            // Load all data in parallel
            const [prefsResult, modulesResult, historyResult] = await Promise.all([
                getPreferences(accessToken),
                getAvailableModules(accessToken),
                getDeliveryHistory(50, accessToken)
            ]);

            setPreferences(prefsResult.data);
            setAvailableModules(modulesResult.data);
            setDeliveryHistory(historyResult.data);

        } catch (error) {
            console.error('Error loading digest preferences:', error);
            message.error('Failed to load digest preferences');
        } finally {
            setLoading(false);
        }
    };

    const handlePreferencesUpdate = async (updates) => {
        setPreferences({ ...preferences, ...updates });
        message.success('Preferences updated successfully');
        await loadData(); // Reload to get fresh data
    };

    const handleSubscriptionChange = async () => {
        message.success('Subscription updated successfully');
        await loadData(); // Reload to get fresh subscriptions
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <Spin size="large" tip="Loading digest preferences..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <Space direction="vertical" size="small" style={{ marginBottom: '24px' }}>
                <Title level={2}>
                    <MailOutlined style={{ marginRight: '12px' }} />
                    Module Digest Preferences
                </Title>
                <Text type="secondary">
                    Manage your automated module digest subscriptions and delivery preferences
                </Text>
            </Space>

            {/* Global enable/disable alert */}
            {preferences && !preferences.GlobalEnabled && (
                <Alert
                    message="Digest Emails Disabled"
                    description="You have disabled all digest emails. Enable them in the Settings tab to start receiving digests."
                    type="warning"
                    showIcon
                    closable
                    style={{ marginBottom: '24px' }}
                />
            )}

            {/* Main content tabs */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    size="large"
                >
                    {/* Subscriptions Tab */}
                    <TabPane
                        tab={
                            <span>
                                <MailOutlined />
                                Subscriptions
                            </span>
                        }
                        key="subscriptions"
                    >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                                <Title level={4}>Module Subscriptions</Title>
                                <Text type="secondary">
                                    Subscribe to receive automated digest emails for specific modules
                                </Text>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            {availableModules.length === 0 ? (
                                <Alert
                                    message="No Modules Available"
                                    description="You don't have access to any modules. Contact your administrator."
                                    type="info"
                                    showIcon
                                />
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                    gap: '16px',
                                    marginTop: '16px'
                                }}>
                                    {availableModules.map(module => (
                                        <ModuleSubscriptionCard
                                            key={module.ModuleId}
                                            module={module}
                                            preferences={preferences}
                                            onSubscriptionChange={handleSubscriptionChange}
                                        />
                                    ))}
                                </div>
                            )}
                        </Space>
                    </TabPane>

                    {/* Settings Tab */}
                    <TabPane
                        tab={
                            <span>
                                <SettingOutlined />
                                Settings
                            </span>
                        }
                        key="settings"
                    >
                        <DeliveryScheduleSettings
                            preferences={preferences}
                            onUpdate={handlePreferencesUpdate}
                        />
                    </TabPane>

                    {/* History Tab */}
                    <TabPane
                        tab={
                            <span>
                                <HistoryOutlined />
                                Delivery History
                            </span>
                        }
                        key="history"
                    >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                                <Title level={4}>Delivery History</Title>
                                <Text type="secondary">
                                    View the history of digest emails sent to you
                                </Text>
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <DeliveryHistoryTable
                                history={deliveryHistory}
                                loading={loading}
                            />
                        </Space>
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
}